/**
 * Recommended max. flight altitude from the ŘLP height grid (POST /uas-zones/height-limit).
 *
 * The server answers with the lowest height of the grid cells (GRID_ATZ, GRID_CTR) the flight area
 * reaches into, with the data of the mission day. It is a recommendation, never a limit: the form
 * fills the field after every change of the flight area, corridor width or circle radius made by the
 * user, keeps a stored or typed value otherwise, and never blocks saving. 0 m is never filled in (the
 * field takes 1–500 m), and without an answer (outside the grid, an error) the field stays as it is.
 */
import { useEffect, useRef, useState } from 'react';
import api from './api';
import { parseCircleRadius, parseCorridorWidth } from './flightArea';

/** Wire values of `status` (HeightLimitStatuses on the server). */
export const HEIGHT_LIMIT_STATUS = {
  OK: 'ok',
  OUTSIDE_GRID: 'outside_grid',
  UNAVAILABLE: 'unavailable',
};

/** Where the value of the altitude field comes from. */
export const ALTITUDE_MODE = {
  AUTO: 'auto',
  MANUAL: 'manual',
  STORED: 'stored',
};

/** What the hint under the altitude field says (see altitudeHint). */
export const ALTITUDE_HINT = {
  DEFAULT: 'default',
  LOADING: 'loading',
  ERROR: 'error',
  OUTSIDE: 'outside',
  ZERO: 'zero',
  AUTO: 'auto',
  MATCHES: 'matches',
  MANUAL: 'manual',
  STORED: 'stored',
  DIFFERS: 'differs',
};

export const heightLimitInputKey = ({ flightArea, corridorWidthM, circleRadiusM, missionDate }) =>
  JSON.stringify([
    flightArea || null, parseCorridorWidth(corridorWidthM), parseCircleRadius(circleRadiusM), missionDate || null,
  ]);

/**
 * Debounced POST /uas-zones/height-limit for the area, width / radius and mission day. Returns
 * { result, loading, failed, invalid, answer } for the current inputs only: `failed` for a
 * network/server failure, `invalid` for a 400 — the flight-area panel already shows why the area is
 * invalid. Without an area nothing is sent. `answer` identifies the finished answer to the latest
 * request (null while it is on its way); every change of the inputs starts a new request, so neither a
 * late answer nor the answer to the same inputs before a change back (A → B → A) counts as current.
 */
export const useHeightGridLimit = ({
  flightArea, corridorWidthM, circleRadiusM, missionDate, enabled = true, delayMs = 500,
}) => {
  const [state, setState] = useState({ key: null, generation: 0, done: false, result: null, failed: false, invalid: false });
  const seq = useRef(0);
  const key = heightLimitInputKey({ flightArea, corridorWidthM, circleRadiusM, missionDate });

  useEffect(() => {
    const generation = ++seq.current;
    const finish = (answer) => setState({ key, generation, done: true, result: null, failed: false, invalid: false, ...answer });
    if (!enabled || !flightArea) {
      finish({});
      return undefined;
    }
    setState({ key, generation, done: false, result: null, failed: false, invalid: false });
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/uas-zones/height-limit', {
          flight_area: flightArea,
          corridor_width_m: parseCorridorWidth(corridorWidthM),
          circle_radius_m: parseCircleRadius(circleRadiusM),
          mission_date: missionDate || null,
        });
        if (generation === seq.current) finish({ result: res.data });
      } catch (err) {
        const invalid = err?.response?.status === 400;
        if (generation === seq.current) finish({ failed: !invalid, invalid });
      }
    }, delayMs);
    return () => clearTimeout(timer);
    // `key` captures every input by value — flightArea is an object whose identity may change on each
    // render of the parent without its content changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, delayMs]);

  // Until the effect of a new key has run, the state still belongs to the previous request.
  const answered = state.key === key && state.done;
  return {
    result: answered ? state.result : null,
    loading: !!flightArea && enabled && !answered,
    failed: answered && state.failed,
    invalid: answered && state.invalid,
    answer: answered ? state.generation : null,
  };
};

/** The altitude to fill in from a result: `max_altitude_m` of an `ok` answer when it is at least 1 m. */
export const recommendedAltitude = (result) => {
  if (result?.status !== HEIGHT_LIMIT_STATUS.OK) return null;
  const value = result.max_altitude_m;
  return Number.isFinite(value) && value >= 1 ? value : null;
};

/**
 * What a settled answer does to the field. `source` is { mode, pendingApply }: `pendingApply` is set by
 * a change of the area or width and dropped by the first answer to it or by typing into the field.
 * The recommendation is filled in when it is pending, or in `auto` mode (the value came from the grid,
 * so another mission day replaces it); `notify` when that overwrites a typed or stored value.
 * Returns { source, value } — `value` undefined = leave the field.
 */
export const nextAltitudeState = ({ source, result, value }) => {
  const mode = source?.mode ?? null;
  const pending = !!source?.pendingApply;
  const limit = recommendedAltitude(result);
  if (limit !== null && (pending || mode === ALTITUDE_MODE.AUTO)) {
    const changed = value !== limit;
    return {
      source: { mode: ALTITUDE_MODE.AUTO, pendingApply: false },
      value: changed ? limit : undefined,
      notify: changed && typeof value === 'number' && (mode === ALTITUDE_MODE.MANUAL || mode === ALTITUDE_MODE.STORED),
    };
  }
  return { source: { mode, pendingApply: false }, value: undefined, notify: false };
};

/**
 * The hint under the altitude field: { kind, limit, cells, partial, above, canApply }. `limit` is the
 * grid height, `partial` part of the area outside the grid, `above` a value over the grid height,
 * `canApply` the "use N m" link (not on a locked area, which cannot change).
 */
export const altitudeHint = ({ source, grid, value, hasArea, locked = false }) => {
  const none = { limit: null, cells: 0, partial: false, above: false, canApply: false };
  if (!hasArea) return { kind: ALTITUDE_HINT.DEFAULT, ...none };
  if (grid.loading) return { kind: ALTITUDE_HINT.LOADING, ...none };
  if (grid.invalid) return { kind: ALTITUDE_HINT.DEFAULT, ...none };
  const result = grid.result;
  if (grid.failed || !result || result.status === HEIGHT_LIMIT_STATUS.UNAVAILABLE) {
    return { kind: ALTITUDE_HINT.ERROR, ...none };
  }
  if (result.status === HEIGHT_LIMIT_STATUS.OUTSIDE_GRID) return { kind: ALTITUDE_HINT.OUTSIDE, ...none };
  if (result.status !== HEIGHT_LIMIT_STATUS.OK) return { kind: ALTITUDE_HINT.ERROR, ...none };
  const limit = result.max_altitude_m;
  const cells = result.cells_count || 0;
  const partial = result.grid_covers_area === false;
  if (!(limit >= 1)) return { kind: ALTITUDE_HINT.ZERO, ...none, limit: 0, cells, partial };

  const mode = source?.mode ?? null;
  const matches = value === limit;
  // An empty field has nothing typed or stored to describe.
  const hasValue = typeof value === 'number';
  const kind = matches
    ? (mode === ALTITUDE_MODE.AUTO ? ALTITUDE_HINT.AUTO : ALTITUDE_HINT.MATCHES)
    : hasValue && mode === ALTITUDE_MODE.MANUAL ? ALTITUDE_HINT.MANUAL
      : hasValue && mode === ALTITUDE_MODE.STORED ? ALTITUDE_HINT.STORED
        : ALTITUDE_HINT.DIFFERS;
  return {
    kind,
    limit,
    cells,
    partial,
    above: hasValue && value > limit,
    canApply: !matches && !locked,
  };
};

/**
 * The altitude field's state in the mission form: the grid answer, where the value comes from and what
 * the hint says. `onValueChange(value)` writes the field, `onOverwrite(value)` reports a typed or stored
 * value replaced after an area change. The form calls `areaChanged()` on every change of the area,
 * width or radius by the user, `typed()` when the user edits the field, and `reset(mode)` when the
 * dialog opens (STORED for an edit, null for a new mission).
 *
 * Each area change has its own revision and is settled by the first finished answer after it: the
 * answer to the new request, or right away the known answer when the inputs did not change (the same
 * area imported again, the same width typed differently) — a pending change never waits for the next
 * change of the mission day.
 */
export const useMissionAltitudeRecommendation = ({
  flightArea, corridorWidthM, circleRadiusM, missionDate, enabled, value, locked = false, onValueChange, onOverwrite,
  delayMs,
}) => {
  const [source, setSource] = useState({ mode: null, pendingApply: false });
  const [areaRevision, setAreaRevision] = useState(0);
  const grid = useHeightGridLimit({ flightArea, corridorWidthM, circleRadiusM, missionDate, enabled, delayMs });

  useEffect(() => {
    if (grid.answer === null) return;
    const next = nextAltitudeState({ source, result: grid.result, value });
    setSource(next.source);
    if (next.value !== undefined) onValueChange(next.value);
    if (next.notify) onOverwrite?.(next.value);
    // Runs once per finished answer, and once per area change that already has its answer; source and
    // value are read as they are at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid.answer, areaRevision]);

  const hint = altitudeHint({ source, grid, value, hasArea: !!flightArea, locked });
  return {
    hint,
    areaChanged: () => {
      setSource((prev) => ({ ...prev, pendingApply: true }));
      setAreaRevision((prev) => prev + 1);
    },
    typed: () => setSource({ mode: ALTITUDE_MODE.MANUAL, pendingApply: false }),
    reset: (mode) => setSource({ mode: mode ?? null, pendingApply: false }),
    apply: () => {
      if (hint.limit === null || hint.limit < 1) return;
      setSource({ mode: ALTITUDE_MODE.AUTO, pendingApply: false });
      onValueChange(hint.limit);
    },
  };
};
